// TimeSlotList.jsx - Updated to prevent removing middle slots
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InfoIcon from '@mui/icons-material/Info';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';

// Custom props that should not be forwarded to DOM
const customProps = ['isAvailable', 'isSelected'];
const shouldForwardProp = (prop) => !customProps.includes(prop);

// Shake animation keyframes
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-5px); }
  30% { transform: translateX(5px); }
  45% { transform: translateX(-5px); }
  60% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  90% { transform: translateX(5px); }
`;

// Hour block container - wider and lower height
const HourBlock = styled(Box)(({ theme, isShaking }) => ({
  width: '100%',
  height: '32px', // Reduced height by ~30%
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0),
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  animation: isShaking ? `${shakeAnimation} 0.4s ease` : 'none',
}));

// Base layer with hour and price
const HourBaseLayer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'space-between', // This ensures the time and price are properly spaced
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
}));

// Half hour slots layer
const HalfHourLayer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  zIndex: 2,
}));

// Individual half hour slot
const HalfHourSlot = styled(Button, { shouldForwardProp })(({ theme, isAvailable, isSelected }) => ({
  flex: 1,
  height: '100%',
  borderRadius: 0,
  backgroundColor: isSelected ? `${theme.palette.primary.main}80` :
    isAvailable ? `${theme.palette.success.light}60` :
      `${theme.palette.grey[300]}60`,
  cursor: isAvailable ? 'pointer' : 'default',
  '&:hover': isAvailable ? {
    backgroundColor: isSelected ? `${theme.palette.primary.main}90` : `${theme.palette.success.main}80`,
  } : {},
  pointerEvents: isAvailable ? 'auto' : 'none',
  transition: 'all 0.2s ease',
}));

// Divider between half hour slots
const HalfHourDivider = styled(Box)(({ theme }) => ({
  width: '1px',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  zIndex: 3,
}));

// Price display for second half
const PriceDisplay = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center', // This helps with horizontal centering
  width: '50%',
  paddingRight: theme.spacing(2),
  textAlign: 'center', // This helps ensure the text itself is centered
  zIndex: 1,
  color: theme.palette.primary.main,
  fontWeight: 'bold',
}));

const TimeSlotList = ({
  slots,
  onSelectSlot,
  room,
  theme,
  allowMultiSelect = false,
  selectedSlot = null,
  selectedDate = null,
  preparingBooking = false,
  doBooking = () => { },
  spaces = 1,
  t // Translation function
}) => {
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalPriceVAT, setTotalPriceVAT] = useState(0);
  const [shakingHourId, setShakingHourId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // Reset selected slots when selectedSlot prop changes
  useEffect(() => {
    if (selectedSlot && !allowMultiSelect) {
      setSelectedSlots([]);
    }
  }, [selectedSlot, allowMultiSelect]);

  // Remove shake effect after animation completes
  useEffect(() => {
    if (shakingHourId) {
      const timer = setTimeout(() => {
        setShakingHourId(null);
      }, 500); // Match the duration of the shake animation

      return () => clearTimeout(timer);
    }
  }, [shakingHourId]);

  // Group slots by hour
  const groupSlotsByHour = (slots) => {
    if (!slots || !slots.length) return [];

    const hourMap = {};

    slots.forEach(slot => {
      const [hour, minute] = slot.start_time.split(':').map(Number);
      const hourKey = `${hour.toString().padStart(2, '0')}:00`;

      if (!hourMap[hourKey]) {
        hourMap[hourKey] = {
          hour: hourKey,
          display: formatTime(hourKey),
          price: null,
          halfHours: [null, null]
        };
      }

      // Determine which half of the hour this is (0 = first half, 1 = second half)
      const halfIndex = minute < 30 ? 0 : 1;
      hourMap[hourKey].halfHours[halfIndex] = slot;

      // If this is the first slot in the hour, use its price as the hour price
      if (hourMap[hourKey].price === null && slot.price) {
        hourMap[hourKey].price = slot.price;
      }
    });

    // Convert to array and sort by hour
    return Object.values(hourMap).sort((a, b) => {
      const hourA = parseInt(a.hour.split(':')[0]);
      const hourB = parseInt(b.hour.split(':')[0]);
      return hourA - hourB;
    });
  };

  // Format time for display (e.g., "9:00" -> "9 AM")
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours} ${period}`;
  };

  // Check if a half-hour slot is selected
  const isHalfHourSelected = (slot) => {
    if (!slot) return false;

    if (!allowMultiSelect && selectedSlot) {
      return selectedSlot.start_time === slot.start_time && selectedSlot.end_time === slot.end_time;
    }

    return selectedSlots.some(selected =>
      selected.start_time === slot.start_time && selected.end_time === slot.end_time
    );
  };

  // Sort slots by time
  const sortSlotsByTime = (slots) => {
    return [...slots].sort((a, b) => {
      const timeA = a.start_time.split(':').map(Number);
      const timeB = b.start_time.split(':').map(Number);

      if (timeA[0] !== timeB[0]) return timeA[0] - timeB[0];
      return timeA[1] - timeB[1];
    });
  };

  // Check if a slot is at the beginning or end of the selection
  const isSlotRemovable = (slot, selectedSlots) => {
    if (!slot || selectedSlots.length <= 1) return true;

    // Sort the slots by start time
    const sortedSlots = sortSlotsByTime(selectedSlots);

    // Get the first and last slot in the selection
    const firstSlot = sortedSlots[0];
    const lastSlot = sortedSlots[sortedSlots.length - 1];

    // A slot is removable if it's the first or last in the sequence
    return (
      (slot.start_time === firstSlot.start_time && slot.end_time === firstSlot.end_time) ||
      (slot.start_time === lastSlot.start_time && slot.end_time === lastSlot.end_time)
    );
  };

  // Handle selecting a half-hour slot
  const handleHalfHourSelect = (slot, hourKey) => {
    if (!slot || !slot.is_available) return;

    if (!allowMultiSelect) {
      // If multi-select is not allowed, just book the slot directly
      // Make sure the slot has the correct price information
      const updatedSlot = {
        ...slot,
        price: slot.price || slot.slot_price || 0,
        price_vat: slot.price_vat || slot.slot_price_vat || (slot.price || slot.slot_price || 0) * 1.2
      };
      onSelectSlot(room, updatedSlot);
      return;
    }

    // Check if slot is already selected
    const isAlreadySelected = isHalfHourSelected(slot);

    if (isAlreadySelected) {
      // Only allow removing if it's the first or last slot in the selection
      if (isSlotRemovable(slot, selectedSlots)) {
        // Remove slot from selection
        const updatedSlots = selectedSlots.filter(
          s => !(s.start_time === slot.start_time && s.end_time === slot.end_time)
        );
        setSelectedSlots(updatedSlots);

        // If this was the last slot, notify parent that no slots are selected
        if (updatedSlots.length === 0) {
          onSelectSlot(room, null);
        }
      } else {
        // Show error if trying to remove a middle slot
        setShakingHourId(hourKey);
        enqueueSnackbar(
          t ? t('booking.message.middle_slot_error') : 'You can only remove slots from the beginning or end of your selection',
          { variant: 'error' }
        );
      }
    } else {
      // Add slot to selection (only if it's consecutive with existing selection)
      const newSelectedSlots = [...selectedSlots, slot];
      const sortedSlots = sortSlotsByTime(newSelectedSlots);

      // Check if slots are consecutive
      const areConsecutive = checkConsecutiveSlots(sortedSlots);

      if (areConsecutive || sortedSlots.length === 1) {
        setSelectedSlots(sortedSlots);

        // Call bookSelectedSlots only when we have valid selections
        if (sortedSlots.length > 0) {
          setTimeout(() => bookSelectedSlots(sortedSlots), 100);
        }
      } else {
        // Trigger shake animation on the hour block
        setShakingHourId(hourKey);

        // Show snackbar notification
        enqueueSnackbar(
          t ? t('booking.message.consecutive_error') : 'Please select consecutive time slots only',
          { variant: 'error' }
        );
      }
    }
  };

  // Check if all slots in an array are consecutive
  const checkConsecutiveSlots = (slots) => {
    if (slots.length <= 1) return true;

    // Sort slots by start time
    const sortedSlots = sortSlotsByTime(slots);

    // Check if each slot's end time matches the next slot's start time
    for (let i = 0; i < sortedSlots.length - 1; i++) {
      if (sortedSlots[i].end_time !== sortedSlots[i + 1].start_time) {
        return false;
      }
    }

    return true;
  };

  // Book all selected slots as a single booking
  const bookSelectedSlots = (slots = selectedSlots) => {
    if (slots.length === 0) return;

    // Calculate total price and VAT
    let totalP = 0;
    let totalPVAT = 0;

    slots.forEach(slot => {
      if (slot.slot_price) {
        totalP += parseFloat(slot.slot_price);
      } else if (slot.price) {
        totalP += parseFloat(slot.price);
      }

      if (slot.slot_price_vat) {
        totalPVAT += parseFloat(slot.slot_price_vat);
      } else if (slot.price_vat) {
        totalPVAT += parseFloat(slot.price_vat);
      } else if (slot.slot_price) {
        totalPVAT += parseFloat(slot.slot_price) * 1.2;
      } else if (slot.price) {
        totalPVAT += parseFloat(slot.price) * 1.2;
      }
    });

    // Create a merged slot object
    const mergedSlot = {
      start_time: slots[0].start_time,
      end_time: slots[slots.length - 1].end_time,
      is_available: true,
      price: totalP,
      price_vat: totalPVAT,
    };

    // Call the parent booking handler
    onSelectSlot(room, mergedSlot);
  };

  // Calculate total price whenever selected slots change
  useEffect(() => {
    let price = 0;
    let priceVAT = 0;
    selectedSlots.forEach(slot => {
      if (slot.slot_price) {
        price += parseFloat(slot.slot_price);
      } else if (slot.price) {
        price += parseFloat(slot.price);
      }

      if (slot.slot_price_vat) {
        priceVAT += parseFloat(slot.slot_price_vat);
      } else if (slot.price_vat) {
        priceVAT += parseFloat(slot.price_vat);
      } else if (slot.slot_price) {
        priceVAT += parseFloat(slot.slot_price) * 1.2;
      } else if (slot.price) {
        priceVAT += parseFloat(slot.price) * 1.2;
      }
    });
    setTotalPrice(price);
    setTotalPriceVAT(priceVAT);

    // If we have selected slots, book them
    if (selectedSlots.length > 0 && allowMultiSelect) {
      bookSelectedSlots();
    }
  }, [selectedSlots]);

  // Group slots by hour
  const hourlySlots = groupSlotsByHour(slots);

  // Calculate actual price for the booking summary
  const calculateActualPrice = () => {
    if (!selectedSlot) return { basePrice: 0, priceWithVAT: 0 };

    // Handle different possible naming conventions for price properties
    const basePrice = parseFloat(selectedSlot.price || selectedSlot.slot_price || 0);
    const priceWithVAT = parseFloat(selectedSlot.price_vat || selectedSlot.slot_price_vat || basePrice * 1.2);

    return { basePrice, priceWithVAT };
  };

  const { basePrice, priceWithVAT } = calculateActualPrice();

  // Calculate Stripe fee
  const calculateStripeFee = (price) => {
    // UK card rate: 1.4% + £0.20
    const fee = Math.max(price * 0.014 + 0.20, 0.20);
    return Math.ceil(fee * 20) / 20; // Round to nearest 5p
  };

  // Calculate duration in a readable format
  const getDuration = () => {
    if (!selectedSlot) return '';

    const startTime = selectedSlot.start_time || '';
    const endTime = selectedSlot.end_time || '';

    if (!startTime || !endTime) return '';

    const getMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const startMinutes = getMinutes(startTime);
    const endMinutes = getMinutes(endTime);
    const durationMinutes = endMinutes - startMinutes;

    if (durationMinutes < 60) {
      return `${durationMinutes} minutes`;
    } else {
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      return minutes ? `${hours} hr ${minutes} min` : `${hours} hr`;
    }
  };

  // Check if booking is allowed
  const canBook = selectedSlot &&
    selectedDate &&
    !dayjs(selectedDate).isBefore(dayjs(), 'day') &&
    basePrice > 0;

  // Get booking status message
  const getBookingStatusMessage = () => {
    if (!selectedSlot) {
      return 'Please select a time slot';
    }

    if (basePrice <= 0) {
      return 'This time slot is not available for booking';
    }

    return 'Ready to book';
  };

  if (!slots || slots.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No time slots available for this room.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.palette.success.light }} />
            <Typography variant="caption">Available</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.palette.grey[300] }} />
            <Typography variant="caption">Booked</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.palette.primary.main }} />
            <Typography variant="caption">Selected</Typography>
          </Box>
        </Box>
      </Box>

      {/* Hour blocks with half-hour slots */}
      <Grid container spacing={2}>
        {hourlySlots.map((hourSlot) => (
          <Grid item xs={4} sm={4} md={4} key={hourSlot.hour}>
            <HourBlock isShaking={shakingHourId === hourSlot.hour}>
              {/* Base layer with hour */}
              <HourBaseLayer>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{
                    width: '50%',          // Take exactly half the width
                    display: 'flex',
                    justifyContent: 'center', // Center text horizontally
                    alignItems: 'center',  // Center text vertically
                    height: '100%',
                    fontSize: '0.9rem',
                  }}
                >
                  {hourSlot.display}
                </Typography>

                {/* Price display - centered in right half */}
                {hourSlot.price && (
                  <Typography
                    variant="caption"
                    sx={{
                      width: '50%',        // Take exactly half the width
                      display: 'flex',
                      justifyContent: 'center', // Center text horizontally
                      alignItems: 'center', // Center text vertically
                      height: '100%',
                      color: theme.palette.primary.main,
                      fontWeight: 'bold'
                    }}
                  >
                    £{hourSlot.price}/hr
                  </Typography>
                )}
              </HourBaseLayer>

              {/* Half hour slots layer */}
              <HalfHourLayer>
                <HalfHourSlot
                  isAvailable={hourSlot.halfHours[0] && hourSlot.halfHours[0].is_available}
                  isSelected={isHalfHourSelected(hourSlot.halfHours[0])}
                  onClick={() => hourSlot.halfHours[0] && handleHalfHourSelect(hourSlot.halfHours[0], hourSlot.hour)}
                  disableRipple={!hourSlot.halfHours[0] || !hourSlot.halfHours[0].is_available}
                />
                <HalfHourDivider />
                <HalfHourSlot
                  isAvailable={hourSlot.halfHours[1] && hourSlot.halfHours[1].is_available}
                  isSelected={isHalfHourSelected(hourSlot.halfHours[1])}
                  onClick={() => hourSlot.halfHours[1] && handleHalfHourSelect(hourSlot.halfHours[1], hourSlot.hour)}
                  disableRipple={!hourSlot.halfHours[1] || !hourSlot.halfHours[1].is_available}
                />
              </HalfHourLayer>
            </HourBlock>
          </Grid>
        ))}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 2 }}>
        Click on either half of an hour block to select a 30-minute slot.
      </Typography>


      {/* New booking summary and confirmation section */}
      <Divider sx={{ my: 3 }} />

      {canBook ? (
        <>
          {/* Processing fee section */}
          <Box sx={{
            mb: 1,
            px: 2,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              Processing fee
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: 'line-through',
                    color: 'text.secondary'
                  }}
                >
                  {calculateStripeFee(priceWithVAT).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary.main"
                  fontWeight={600}
                >
                  £0.00
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Summary and booking button */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1
          }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={500}>
                {room?.name || 'Meeting Room'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSlot ? `${selectedSlot.start_time} - ${selectedSlot.end_time}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getDuration() ? ` (${getDuration()})` : ''}
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}>
                <Typography variant="h5" color="primary.main" fontWeight={600}>
                  {basePrice.toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: -0.5,
                    fontSize: '0.7rem'
                  }}
                >
                  {priceWithVAT.toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  })} VAT inc.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={doBooking}
                disabled={preparingBooking}
                sx={{
                  minWidth: { xs: '80px', sm: '100px' }
                }}
              >
                {preparingBooking ? <CircularProgress size={20} /> : 'Confirm'}
              </Button>
            </Box>
          </Box>

          {/* Notification about booking confirmation */}
          {selectedSlot && !selectedSlot.instant_booking && (
            <Box sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{
                fontSize: '1.2rem',
                color: theme.palette.info.main,
                display: 'flex',
                alignItems: 'center'
              }}>
                <InfoIcon fontSize="inherit" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Your booking will need to be confirmed by the host. You&apos;ll receive notification once it&apos;s approved, typically within 24 hours.
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box className="booking-status" sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {getBookingStatusMessage()}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default TimeSlotList;